import { Theme, ThemeOptions, alpha, buttonClasses, dialogClasses } from '@mui/material'

export default (theme: Theme): ThemeOptions['components'] => ({
    MuiDialog: {
        defaultProps: {
            PaperProps: {
                variant: 'elevation',
            },
        },
        styleOverrides: {
            paperWidthXs: {
                maxWidth: 510,
            },
            paperWidthSm: {
                maxWidth: 650,
            },
            paperFullScreen: {
                maxWidth: '100%',
            },
            root: {
                overflow: 'hidden',

                '.DsDialogHeader-root': {
                    padding: theme.spacing(3),
                    paddingBottom: theme.spacing(3),
                },
                '.DsDialogHeader-closeButtonWrapper': {
                    position: 'absolute',
                    right: theme.spacing(1),
                    top: theme.spacing(1),
                    zIndex: 1,
                },
                [`&.DsDialog-fullHeight .${dialogClasses.paperScrollPaper}`]: {
                    height: '100%', // Will be capped to (100% - 64px) by the max-height rule on paperScrollPaper.
                },
            },
        },
    },
    MuiDialogContent: {
        styleOverrides: {
            root: {
                margin: 0,
                padding: '24px',

                // Shadows that appear when scrolling the content area
                background: `
                        /* Cover for the top shadow */
                        linear-gradient(
                            white 30%,
                            rgba(255, 255, 255, 0)
                        ) center top,

                        /* Cover for the bottom shadow */
                        linear-gradient(
                            rgba(255, 255, 255, 0),
                            white 70%
                        ) center bottom,

                        /* Top shadow */
                        radial-gradient(
                            farthest-side at 50% 0,
                            ${alpha(theme.palette.grey[500], 0.25)},
                            rgba(0, 0, 0, 0)
                        ) center top,

                        /* Bottom shadow */
                        radial-gradient(
                            farthest-side at 50% 100%,
                            ${alpha(theme.palette.grey[500], 0.25)},
                            rgba(0, 0, 0, 0)
                        ) center bottom;`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: '200% 5px, 200% 5px, 200% 5px, 200% 5px',
                backgroundAttachment: 'local, local, scroll, scroll',

                // By default the content area is allowed to shrink to make the actions sticky, but this
                // doesn't make sense when vertical space is tight (e.g. when virtual keyboard is open).
                '@media (max-height: 320px)': {
                    flexShrink: 0,
                },
                [`&.${dialogClasses.container}`]: {
                    backgroundColor: theme.palette.success.main,
                },
                '&.DsDialogContent-noTopPadding': {
                    paddingTop: 0,
                },
                '&.DsDialogContent-noBottomPadding': {
                    paddingBottom: 0,
                },
            },
        },
    },
    MuiDialogActions: {
        styleOverrides: {
            root: {
                padding: 24,

                // MUI uses :first-of-type to add spacing, which doesn't work
                // if the primary button is a link.
                // Cannot use first-child because of Emotion:
                // https://infinum.com/handbook/frontend/react/common-ssr-errors
                [`& > .${buttonClasses.root}`]: {
                    marginLeft: 0, // undo MUI styling
                    marginRight: 8,
                },
                '& > :last-child': {
                    marginRight: 0,
                },

                [theme.breakpoints.down('sm')]: {
                    zIndex: 1,
                    justifyContent: 'center',
                    paddingTop: theme.spacing(2),
                    paddingBottom: theme.spacing(2),

                    // Expand buttons
                    [`& .${buttonClasses.root}`]: {
                        width: '100%',
                    },
                },
            },
        },
    },
})
